/**
 * @fileoverview Vuex store module for Email Sequences.
 */

import {
  getSequences,
  getSequence,
  createSequence,
  updateSequence,
  deleteSequence,
  activateSequence,
  pauseSequence,
  enrollSubscribers,
  getEnrollments,
  removeEnrollment,
  getSequenceStats,
} from "@/api/email_sequences";

const state = {
  sequences: [],
  currentSequence: null,
  enrollments: [],
  stats: null,
  loading: false,
};

const mutations = {
  setSequences(state, sequences) {
    state.sequences = sequences;
  },
  setCurrentSequence(state, sequence) {
    state.currentSequence = sequence;
  },
  addSequence(state, sequence) {
    state.sequences.push(sequence);
  },
  updateSequence(state, updated) {
    const id = updated._id || updated.id;
    const idx = state.sequences.findIndex(s => (s._id || s.id) === id);
    if (idx !== -1) Object.assign(state.sequences[idx], updated);
  },
  removeSequence(state, id) {
    state.sequences = state.sequences.filter(s => (s._id || s.id) !== id);
  },
  setEnrollments(state, enrollments) {
    state.enrollments = enrollments;
  },
  setStats(state, stats) {
    state.stats = stats;
  },
  setLoading(state, val) {
    state.loading = val;
  },
};

const actions = {
  async loadSequences({ commit }) {
    commit('setLoading', true);
    try {
      const sequences = await getSequences();
      commit('setSequences', Array.isArray(sequences) ? sequences : []);
    } catch (e) {
      console.error('Failed to load sequences:', e);
    } finally {
      commit('setLoading', false);
    }
  },

  async loadSequence({ commit }, id) {
    try {
      const sequence = await getSequence(id);
      commit('setCurrentSequence', sequence);
      return sequence;
    } catch (e) {
      console.error('Failed to load sequence:', e);
    }
  },

  async saveSequence({ commit }, data) {
    const sequence = await createSequence(data);
    commit('addSequence', sequence);
    return sequence;
  },

  async editSequence({ commit }, { id, data }) {
    const updated = await updateSequence(id, data);
    commit('updateSequence', updated);
    return updated;
  },

  async deleteSequence({ commit }, id) {
    await deleteSequence(id);
    commit('removeSequence', id);
  },

  async activateSequence({ commit }, id) {
    const updated = await activateSequence(id);
    commit('updateSequence', updated);
    return updated;
  },

  async pauseSequence({ commit }, id) {
    const updated = await pauseSequence(id);
    commit('updateSequence', updated);
    return updated;
  },

  async enrollSubscribers(_, { id, data }) {
    return await enrollSubscribers(id, data);
  },

  async loadEnrollments({ commit }, { id, status }) {
    const enrollments = await getEnrollments(id, status);
    commit('setEnrollments', Array.isArray(enrollments) ? enrollments : []);
    return enrollments;
  },

  async removeEnrollment(_, { id, email }) {
    return await removeEnrollment(id, email);
  },

  async loadStats({ commit }, id) {
    const stats = await getSequenceStats(id);
    commit('setStats', stats);
    return stats;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
